import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { profileService } from '../../services/profileService';
import { useAuth } from '../../context/AuthContext';
import './ProfileDetail.css';

const ProfileDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadProfileData();
  }, [id]);

  const loadProfileData = async () => {
    try {
      setLoading(true);
      const [profileResponse, skillsResponse] = await Promise.all([
        profileService.getProfile(id),
        profileService.getProfileSkills(id)
      ]);
      
      // Unwrap responses
      const profileData = profileResponse.data || profileResponse;
      const skillsData = skillsResponse.data || skillsResponse;
      
      setProfile(profileData);
      setSkills(Array.isArray(skillsData) ? skillsData : []);
    } catch (err) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this profile?')) return;

    try {
      await profileService.deleteProfile(id);
      navigate('/profiles');
    } catch (err) {
      setError(err.message || 'Failed to delete profile');
    }
  };

  const handleGenerateCV = async (format) => {
    try {
      await profileService.generateCV(id, format);
    } catch (err) {
      setError(err.message || 'Failed to generate CV');
    }
  };

  if (loading) {
    return <div className="loading">Loading profile...</div>;
  }

  if (error && !profile) {
    return <div className="error-message">{error}</div>;
  }

  if (!profile) {
    return <div className="loading">Loading profile...</div>;
  }

  const canEdit = user?.role === 'hr_admin' || user?.role === 'admin' || user?.role === 'manager';

  return (
    <div className="profile-detail-container">
      <div className="profile-header">
        <div className="profile-header-left">
          <button onClick={() => navigate('/profiles')} className="btn-back">
            ← Back to Profiles
          </button>
          <h1>{profile.full_name || `${profile.first_name} ${profile.last_name}`.trim()}</h1>
          <p className="profile-position">{profile.designation || profile.position}</p>
        </div>
        <div className="profile-header-actions">
          {canEdit && (
            <>
              <button onClick={() => navigate(`/profiles/${id}/edit`)} className="btn-secondary">
                Edit Profile
              </button>
              <button onClick={handleDelete} className="btn-danger">
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="profile-content">
        <div className="profile-main">
          <section className="profile-section">
            <h2>Contact Information</h2>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Email:</span>
                <span className="info-value">{profile.email}</span>
              </div>
              {profile.phone && (
                <div className="info-item">
                  <span className="info-label">Phone:</span>
                  <span className="info-value">{profile.phone}</span>
                </div>
              )}
              {profile.location && (
                <div className="info-item">
                  <span className="info-label">Location:</span>
                  <span className="info-value">{profile.location}</span>
                </div>
              )}
              {profile.department && (
                <div className="info-item">
                  <span className="info-label">Department:</span>
                  <span className="info-value">{profile.department}</span>
                </div>
              )}
            </div>
          </section>

          {profile.bio && (
            <section className="profile-section">
              <h2>About</h2>
              <p className="profile-bio">{profile.bio}</p>
            </section>
          )}

          <section className="profile-section">
            <h2>Skills</h2>
            {skills.length > 0 ? (
              <div className="skills-grid">
                {skills.map(skill => (
                  <div key={skill.id} className="skill-card">
                    <div className="skill-header">
                      <h3>{skill.skill_name}</h3>
                      <span className={`skill-level level-${skill.proficiency_level}`}>
                        {skill.proficiency_level}
                      </span>
                    </div>
                    {skill.years_of_experience && (
                      <p className="skill-experience">{skill.years_of_experience} years</p>
                    )}
                    {skill.last_used_date && (
                      <p className="skill-last-used">Last used: {new Date(skill.last_used_date).toLocaleDateString()}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-data">No skills added yet</p>
            )}
            {canEdit && (
              <button onClick={() => navigate(`/profiles/${id}/skills`)} className="btn-secondary">
                Manage Skills
              </button>
            )}
          </section>
        </div>

        <div className="profile-sidebar">
          <section className="sidebar-section">
            <h3>Links</h3>
            <div className="links-list">
              {profile.linkedin_url && (
                <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer" className="profile-link">
                  LinkedIn →
                </a>
              )}
              {profile.github_url && (
                <a href={profile.github_url} target="_blank" rel="noopener noreferrer" className="profile-link">
                  GitHub →
                </a>
              )}
              {profile.portfolio_url && (
                <a href={profile.portfolio_url} target="_blank" rel="noopener noreferrer" className="profile-link">
                  Portfolio →
                </a>
              )}
              {!profile.linkedin_url && !profile.github_url && !profile.portfolio_url && (
                <p className="no-data">No links added</p>
              )}
            </div>
          </section>

          {profile.resume_path && (
            <section className="sidebar-section">
              <h3>Resume</h3>
              <a href={profile.resume_path} target="_blank" rel="noopener noreferrer" className="btn-secondary btn-block">
                View Resume
              </a>
            </section>
          )}

          <section className="sidebar-section">
            <h3>Generate CV</h3>
            <div className="cv-actions">
              <button onClick={() => handleGenerateCV('html')} className="btn-primary btn-block">
                View CV
              </button>
            </div>
            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
              Opens CV in new tab. Use browser's Print to save as PDF.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ProfileDetail;
